import type { LabeledGraphicPoint } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import { rtValueToText } from '@vedalib/rich-text'
import * as R from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useCallback } from 'react'
import React from 'react'

import FloatingPanel from 'components/Editor/FloatingPanel'
import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import Padding from 'components/Editor/Toolbar/Padding'
import { useFormData } from 'components/formKit/hooks'
import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import Button, { IconButton, TileButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { useElementStyleCss } from 'services/Branding/hooks'
import ScrollService, { scrollProps } from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { setElement } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getActiveElementId,
  getDeviceMode,
  getElementState,
} from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { getOriginalImageById } from 'utils/files'

import * as s from './LabeledGraphicFormValueItem.module.scss'
import FloatingPanelMap, { useDefaultIcon, POINT_IMG } from './LabeledGraphicFormValueItemFloating'
import type { LabeledGraphicEditorElement, LabeledGraphicState } from './labeledGraphicTypes'

const LabeledGraphicPointFormValue: React.FC<IRenderItemProps<LabeledGraphicPoint>> = ({
  value: array,
  index,
  item,
  name,
  drag,
  min,
  max,
  onChange,
}) => {
  const dispatch = useProjectDispatch()
  const { style, value } = useFormData<LabeledGraphicEditorElement>(name, 2) || {}
  const device = useProjectContext(getDeviceMode)
  const [form, setForm] = useState<'root' | 'icon'>('root')
  const brandIcon = useDefaultIcon({ name: `${name}.${index}. ${POINT_IMG.name}` })
  const elementStyles = useElementStyleCss('labeledGraphic', device, style)
  const elementId = useProjectContext(getActiveElementId)
  const state = useProjectContext(getElementState, elementId || '')
  const isActive = item.value === state?.activePoint
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)
  const defaultIcon = getOriginalImageById(brandIcon.defaultValue?.id)
  const iconStyle = useMemo(
    () => ({ color: '#ffffff', backgroundColor: elementStyles?.point?.backgroundColor }),
    [elementStyles?.point?.backgroundColor],
  )
  const isIcon = value?.type === 'image'

  const setState = useCallback(
    (value: LabeledGraphicState) => {
      if (elementId) {
        dispatch(setElement({ id: elementId, value }))
      }
    },
    [dispatch, elementId],
  )

  const handleBlur = useCallback(() => setState({ activePoint: null }), [setState])

  const handleFocus = useCallback(
    () => setState({ activePoint: item.value }),
    [setState, item.value],
  )

  const onClone = useCallback(() => {
    const clone = { ...R.clone(array[index]), value: generateId() }
    return onChange?.(R.insert(index + 1, clone, array), { command: 'clone', item: array[index] })
  }, [onChange, array, index])

  const onDelete = useCallback(() => {
    return onChange?.(
      array.filter((_item, i) => i !== index),
      { command: 'delete', item: array[index] },
    )
  }, [onChange, array, index])

  const handleClose = useCallback(() => setForm('root'), [])

  useEffect(() => {
    if (isActive) {
      ScrollService.scroll({ container: ScrollContainerEnum.toolbar, id: item.value }, null)
    }
  }, [isActive, item.value])

  const Header = FloatingPanelMap[form].header
  const Panel = FloatingPanelMap[form].panel

  return (
    <Button
      name={`point-${index}`}
      styleType='free'
      {...scrollProps(item.value)}
      className={s.root}
      onBlur={handleBlur}
      onClick={handleFocus}
    >
      <div className={s.header} ref={drag}>
        <span className={s.title}>
          <Icon name='builderDrugAndDrop' size={KitSize.S} />
          {t('input.label.point')} {index + 1}
        </span>
        <span className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
            <IconButton
              disabled={disableClone}
              icon='otherCopy'
              name='clone'
              onClick={onClone}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name='delete'
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </span>
      </div>
      <Padding gap>
        <FloatingPanel
          header={<Header index={index} item={item} name={name} setForm={setForm} />}
          onClose={handleClose}
          panel={<Panel index={index} item={item} name={name} setForm={setForm} />}
        >
          {({ open }) => (
            <TileButton active={open} icon='otherAdd' name='settings' styleType='ghost'>
              {isIcon && (
                <div className={s.icons}>
                  <PanelIconImage
                    defaultPath={defaultIcon?.path}
                    file={item.pointIcon}
                    icon='otherAdd'
                    iconSize={KitSize.S}
                    iconStyle={iconStyle}
                  />
                  <PanelIconImage file={item.image} icon='otherBlock' />
                </div>
              )}
              {!isIcon && <PanelIconImage file={item.image} icon='otherBlock' />}
              <Typography rows={1} styleType='hint'>
                {rtValueToText(item.description)}
              </Typography>
            </TileButton>
          )}
        </FloatingPanel>
      </Padding>
    </Button>
  )
}

export default React.memo(LabeledGraphicPointFormValue) as typeof LabeledGraphicPointFormValue
